/**
 * redux/slices/banks.ts — Bank Submission Status data + drill-in loads.
 *
 * The page-level fetcher is `fetchSubmissionStatus`. The per-bank drill-in
 * uses `fetchBankLoads`, parameterised by orgId.
 */

import { createSlice, createAsyncThunk, type PayloadAction } from '@reduxjs/toolkit';

import * as apiClient from '../../services/api';
import type {
	Bank,
	BankLoadEntry,
	BankSubmissionResponse,
	BankSubmissionRow,
} from '../../type/api';
import type { RootState } from '../store';

export interface BanksState {
	list: Bank[];
	submissionStatus: BankSubmissionRow[];
	asOf: string | null;
	status: 'idle' | 'loading' | 'success' | 'error';
	error: string | null;

	loads: Record<number, BankLoadEntry[]>;
	loadsStatus: Record<number, 'idle' | 'loading' | 'success' | 'error'>;
	loadsError: Record<number, string | null>;
}

const INITIAL: BanksState = {
	list: [],
	submissionStatus: [],
	asOf: null,
	status: 'idle',
	error: null,

	loads: {},
	loadsStatus: {},
	loadsError: {},
};

// ── Thunks ──────────────────────────────────────────────────────────────

export const fetchBanks = createAsyncThunk<{ items: Bank[] }, void, { rejectValue: string }>(
	'banks/fetchAll',
	async (_, { rejectWithValue }) => {
		try {
			return await apiClient.getBanks();
		} catch (err: unknown) {
			return rejectWithValue(err instanceof Error ? err.message : 'Failed to load banks');
		}
	},
);

export const fetchSubmissionStatus = createAsyncThunk<
	BankSubmissionResponse,
	void,
	{ rejectValue: string }
>('banks/fetchSubmissionStatus', async (_, { rejectWithValue }) => {
	try {
		return await apiClient.getBankSubmissionStatus();
	} catch (err: unknown) {
		return rejectWithValue(err instanceof Error ? err.message : 'Failed to load submission status');
	}
});

export const fetchBankLoads = createAsyncThunk<
	{ orgId: number; loads: BankLoadEntry[] },
	{ orgId: number; from?: string; to?: string },
	{ rejectValue: { orgId: number; message: string } }
>('banks/fetchLoads', async (args, { rejectWithValue }) => {
	try {
		const res = await apiClient.getBankLoads(args.orgId, args.from, args.to);
		return { orgId: args.orgId, loads: res.loads };
	} catch (err: unknown) {
		return rejectWithValue({
			orgId: args.orgId,
			message: err instanceof Error ? err.message : 'Failed to load history',
		});
	}
});

// ── Slice ───────────────────────────────────────────────────────────────

const banksSlice = createSlice({
	name: 'banks',
	initialState: INITIAL,
	reducers: {
		clearLoadsError(state, action: PayloadAction<number>) {
			state.loadsError[action.payload] = null;
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(fetchBanks.fulfilled, (state, action) => {
				state.list = action.payload.items;
			})
			.addCase(fetchSubmissionStatus.pending, (state) => {
				state.status = 'loading';
				state.error = null;
			})
			.addCase(fetchSubmissionStatus.fulfilled, (state, action) => {
				state.submissionStatus = action.payload.banks;
				state.asOf = action.payload.asOf;
				state.status = 'success';
				state.error = null;
			})
			.addCase(fetchSubmissionStatus.rejected, (state, action) => {
				state.status = 'error';
				state.error = action.payload || 'Failed to load submission status';
			})
			.addCase(fetchBankLoads.pending, (state, action) => {
				const id = action.meta.arg.orgId;
				state.loadsStatus[id] = 'loading';
				state.loadsError[id] = null;
			})
			.addCase(fetchBankLoads.fulfilled, (state, action) => {
				state.loads[action.payload.orgId] = action.payload.loads;
				state.loadsStatus[action.payload.orgId] = 'success';
			})
			.addCase(fetchBankLoads.rejected, (state, action) => {
				const id = action.payload?.orgId ?? action.meta.arg.orgId;
				state.loadsStatus[id] = 'error';
				state.loadsError[id] = action.payload?.message || 'Failed to load history';
			});
	},
});

export const { clearLoadsError } = banksSlice.actions;
export default banksSlice.reducer;

// ── Selectors ───────────────────────────────────────────────────────────

export const selectBanks = (s: RootState): Bank[] => s.banks.list;
export const selectSubmissionStatus = (s: RootState): BankSubmissionRow[] =>
	s.banks.submissionStatus;
export const selectSubmissionAsOf = (s: RootState): string | null => s.banks.asOf;
export const selectSubmissionStatusState = (s: RootState): BanksState['status'] => s.banks.status;
export const selectSubmissionStatusError = (s: RootState): string | null => s.banks.error;

export const selectBankLoads =
	(orgId: number) =>
	(s: RootState): BankLoadEntry[] | undefined =>
		s.banks.loads[orgId];

export const selectBankLoadsStatus =
	(orgId: number) =>
	(s: RootState): 'idle' | 'loading' | 'success' | 'error' =>
		s.banks.loadsStatus[orgId] || 'idle';

export const selectBankLoadsError =
	(orgId: number) =>
	(s: RootState): string | null =>
		s.banks.loadsError[orgId] || null;
